import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  Tooltip,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add,
  Delete,
  Business,
  Group,
  Storage,
  Email,
  Close
} from '@mui/icons-material';
import { fetchClients, createClient, deleteClient, fetchOrganizations, fetchDatabases, fetchUsers } from '../api/admin';
import { Client, ClientCreateInput, Organization } from '../types/admin';
import { useUser } from './UserContext';

const THEME = {
  BG_PRIMARY: "#000000",
  BG_SECONDARY: "#1a1a1a",
  BG_TERTIARY: "#2d2d2d",
  SURFACE_GLASS: "rgba(255, 255, 255, 0.03)",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  TEXT_MUTED: "#808080",
  ACCENT_PRIMARY: "#2196f3",
  ACCENT_SUCCESS: "#4caf50",
  ACCENT_ERROR: "#ff4444",
  ACCENT_WARNING: "#ff9800",
  BORDER_SUBTLE: "#333333",
  BORDER_DEFAULT: "#555555",
};

interface ClientsManagerProps {
  onClose?: () => void;
}

export const ClientsManager: React.FC<ClientsManagerProps> = ({ onClose }) => {
  const { user } = useUser();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'superadmin';
  
  const [clients, setClients] = useState<Client[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  
  const [formData, setFormData] = useState<ClientCreateInput>({
    name: '',
    contact_email: '',
    organization_id: 0
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [clientsData, usersData] = await Promise.all([
        fetchClients(),
        fetchUsers()
      ]);
      
      let databasesData: any[] = [];
      try {
        databasesData = await fetchDatabases();
      } catch (dbErr: any) {
        // If no access to databases, continue without them
        if (dbErr.response?.status !== 403) {
          // Error loading databases
        }
      }
      
      // Calculate user and database counts for each client
      const clientsWithCounts = clientsData.map(client => {
        const clientUsers = usersData.filter(user => user.client_id === client.id);
        const clientDatabases = databasesData.filter(db => db.client_id === client.id);
        
        return {
          ...client,
          users_count: clientUsers.length,
          databases_count: clientDatabases.length
        };
      });
      
      // Фильтруем клиентов для Admin
      let filteredClients = clientsWithCounts;
      if (!isSuperAdmin && user?.organization_id) {
        // Admin видит только клиентов своей организации
        filteredClients = clientsWithCounts.filter(
          (client) => client.organization_id === user.organization_id
        );
      }
      
      setClients(filteredClients);
      
      // Организации загружаем только для Super Admin
      if (isSuperAdmin) {
        const orgsData = await fetchOrganizations();
        setOrganizations(orgsData);
      }
      
      setError(null);
    } catch (err: any) {
      // Более детальная обработка ошибки
      if (err.response?.status === 403) {
        setError('Accesso negato. Contatta il Super Admin.');
      } else {
        setError(err.response?.data?.detail || 'Impossibile caricare i dati');
      }
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, user?.organization_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getOrganizationName = (orgId: number) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Sconosciuta';
  };

  const handleCreateClient = async () => {
    if (!formData.name.trim()) {
      setFormError('Il nome è obbligatorio');
      return;
    }
    if (!formData.organization_id) {
      setFormError('Seleziona un\'organizzazione');
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      await createClient(formData);
      setCreateDialogOpen(false);
      setFormData({ name: '', contact_email: '', organization_id: 0 });
      await loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;

    try {
      setSubmitting(true);
      await deleteClient(selectedClient.id);
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nell\'eliminazione');
      setDeleteDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: THEME.ACCENT_PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
            🏢 Gestione Clienti
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
            {isSuperAdmin ? 'Gestisci i clienti e le loro informazioni' : 'Visualizza i clienti della tua organizzazione'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isSuperAdmin && (
            <Tooltip title="Crea nuovo cliente">
              <IconButton
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.15),
                  color: THEME.ACCENT_SUCCESS,
                  '&:hover': {
                    bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.25),
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                <Add />
              </IconButton>
            </Tooltip>
          )}
          
          {onClose && (
            <Tooltip title="Chiudi">
              <IconButton 
                onClick={onClose}
                sx={{ 
                  color: THEME.TEXT_SECONDARY,
                  '&:hover': { 
                    bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
                    color: THEME.ACCENT_ERROR
                  }
                }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {clients.map((client) => (
          <Grid item xs={12} md={6} lg={4} key={client.id}>
            <Fade in timeout={300}>
              <Card
                sx={{
                  bgcolor: THEME.BG_SECONDARY,
                  border: `1px solid ${THEME.BORDER_SUBTLE}`,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: THEME.ACCENT_PRIMARY,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 16px ${alpha(THEME.ACCENT_PRIMARY, 0.2)}`
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Business sx={{ color: THEME.ACCENT_PRIMARY, fontSize: 32 }} />
                    {isSuperAdmin && (
                      <Tooltip title="Elimina cliente">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedClient(client);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{
                            color: THEME.ACCENT_ERROR,
                            '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.1) }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
                    {client.name}
                  </Typography>

                  {client.contact_email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Email sx={{ fontSize: 16, color: THEME.TEXT_SECONDARY }} />
                      <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                        {client.contact_email}
                      </Typography>
                    </Box>
                  )}

                  <Chip
                    label={getOrganizationName(client.organization_id)}
                    size="small"
                    sx={{
                      bgcolor: alpha(THEME.ACCENT_WARNING, 0.2),
                      color: THEME.ACCENT_WARNING,
                      mb: 2
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    <Chip
                      icon={<Group />}
                      label={`${client.users_count || 0} Utenti`}
                      size="small"
                      sx={{
                        bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.1),
                        color: THEME.ACCENT_SUCCESS,
                        border: `1px solid ${alpha(THEME.ACCENT_SUCCESS, 0.3)}`
                      }}
                    />
                    <Chip
                      icon={<Storage />}
                      label={`${client.databases_count || 0} Database`}
                      size="small"
                      sx={{
                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1),
                        color: THEME.ACCENT_PRIMARY,
                        border: `1px solid ${alpha(THEME.ACCENT_PRIMARY, 0.3)}`
                      }}
                    />
                  </Box>

                  <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, display: 'block', mt: 2 }}>
                    Creato: {new Date(client.created_at).toLocaleDateString('it-IT')}
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {clients.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Business sx={{ fontSize: 64, color: THEME.TEXT_SECONDARY, mb: 2 }} />
          <Typography variant="h6" sx={{ color: THEME.TEXT_SECONDARY }}>
            Nessun cliente trovato
          </Typography>
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !submitting && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Nuovo Cliente
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Nome Cliente"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{
              mb: 2,
              mt: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Email di Contatto"
            type="email"
            fullWidth
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
          />
          <FormControl
            fullWidth
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
          >
            <InputLabel>Organizzazione</InputLabel>
            <Select
              value={formData.organization_id}
              onChange={(e) => setFormData({ ...formData, organization_id: Number(e.target.value) })}
              label="Organizzazione"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: THEME.BG_SECONDARY,
                    border: `1px solid ${THEME.BORDER_SUBTLE}`,
                    '& .MuiMenuItem-root': {
                      color: THEME.TEXT_PRIMARY,
                      '&:hover': {
                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1)
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.15),
                        '&:hover': {
                          bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2)
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value={0} disabled>Seleziona un'organizzazione</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name} {org.is_root && '(ROOT)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setFormData({ name: '', contact_email: '', organization_id: 0 });
              setFormError(null);
            }}
            disabled={submitting}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleCreateClient}
            variant="contained"
            disabled={submitting}
            sx={{
              bgcolor: THEME.ACCENT_PRIMARY,
              '&:hover': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.8) }
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Conferma Eliminazione
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: THEME.TEXT_SECONDARY }}>
            Sei sicuro di voler eliminare il cliente <strong>{selectedClient?.name}</strong>?
          </Typography>
          {selectedClient && (selectedClient.users_count || 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Questo cliente ha {selectedClient.users_count} utenti associati. Rimuovili prima di eliminare il cliente.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedClient(null);
            }}
            disabled={submitting}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDeleteClient}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
